import React, { useRef, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarked, faPhoneAlt, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { formatMessage } from '../../i18n/language';
import { fallbackTextProp, linkToWhatsapp } from '../../services/utils';
import { getStatus } from './status';

import './styles.scss';

const DeliveryRequestListItem = ({ deliveryOrder, handleStart, handleFinish, handlePause }) => {
  // eslint-disable-next-line no-unused-vars
  const [show, setShow] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [target, setTarget] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const ref = useRef(null);
  const {
    externalId,
    clientName,
    providerName,
    providerPhone = '',
    clientPhone = '',
    destinationAddress = '',
    workOrderUrl = '',
    startTime
  } = deliveryOrder;
  const project = deliveryOrder.project || {};
  const { externalId: projectExternalId, name } = project;
  const status = deliveryOrder.status || 0;
  const destinationIsPresent = !!destinationAddress && destinationAddress.length > 0;
  const providerIsPresent = !!providerName;
  const providerOrClientLabel = providerIsPresent ? 'delivery.provider' : 'delivery.client';
  const addressLabel = 'delivery.address';
  const notesLabel = 'delivery.notes';
  const providerOrClientName = providerIsPresent ? providerName : clientName;
  let phoneIsPresent;
  let presentPhone;
  if (providerIsPresent) {
    phoneIsPresent = !!providerPhone && providerPhone.length > 4;
    presentPhone = phoneIsPresent ? providerPhone : null;
  } else {
    phoneIsPresent = !!clientPhone && clientPhone.length > 4;
    presentPhone = phoneIsPresent ? clientPhone : null;
  }

  const drawPauseButton = () => {
    const isRunning = status === 1;
    return isRunning ? (
      <Button variant="warning" className="mt-3 col-12" onClick={() => handlePause(externalId)}>
        {formatMessage({ id: 'delivery.pause' })}
      </Button>
    ) : null;
  };

  const drawActionButton = () => {
    const drawButton = status === 0 || status === 1;
    const statusMessageId = status === 0 ? 'delivery.start' : 'delivery.finalize';
    const buttonColor = status === 0 ? 'secoundary' : 'primary';
    const buttonHandler = status === 0 ? handleStart : handleFinish;
    return drawButton ? (
      <Button variant={buttonColor} className="mt-3 col-12" onClick={() => buttonHandler(externalId)}>
        {formatMessage({ id: statusMessageId })}
      </Button>
    ) : null;
  };

  const handleWorkOrderButton = () => {
    if (workOrderUrl) {
      window.open(workOrderUrl);
    }
  };

  const formatStartTime = () => {
    return new Date(startTime).toLocaleTimeString('es', {
      timeStyle: 'short'
    });
  };

  const drawHeader = () => {
    return (
      <Row className="project-name">
        <Col className="label" style={{ maxHeight: 46, overflow: 'hidden' }}>
          {projectExternalId} {name ? '-' : ` - ${formatMessage({ id: 'project.noNameAbbreviated' })}`} {name}
        </Col>
        <Col xs="auto" className="px-0">
          <div>
            <div className={`btn-status text-center ${getStatus(status)}`}>
              <p className="paragraph1 mb-0" style={{ fontSize: '10px' }}>
                {formatMessage({ id: `delivery.${getStatus(status)}` })}
              </p>
            </div>
          </div>
        </Col>
        <Col xs="auto" className="px-1">
          <FontAwesomeIcon
            icon={faClipboardCheck}
            style={{ width: 22, height: 22 }}
            className={workOrderUrl ? 'icon-clickable' : 'icon-disabled'}
            onClick={handleWorkOrderButton}
          />
        </Col>
      </Row>
    );
  };

  const handleMapButton = () => {
    if (destinationIsPresent) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destinationAddress)}`);
    }
  };

  const handleTelButton = () => {
    if (phoneIsPresent) {
      window.open('tel:9971863733');
    }
  };

  const handleWhatsappButton = () => {
    if (phoneIsPresent) {
      window.open(linkToWhatsapp(presentPhone));
    }
  };

  return (
    <Col xs={12} lg={4} md={6} className="mt-3 inner-card ">
      <div className="d-flex flex-column" style={{ height: '100%' }}>
        {drawHeader()}
        <Row>
          <Col xs={8}>
            <p className="label mb-0">{formatMessage({ id: providerOrClientLabel })}</p>
          </Col>
          <Col xs={4}>
            <p className="label mb-0 text-right">{formatStartTime()}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <p className="paragraph2 mb-1">
              {fallbackTextProp(providerOrClientName, formatMessage({ id: 'unavailable' }))}
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <p className={`paragraph2 mb-1 ${phoneIsPresent ? 'text-clip-nowrap' : ''}`}>
              {fallbackTextProp(presentPhone, formatMessage({ id: 'unavailable' }))}
            </p>
          </Col>
          <Col xs={4} className="text-right d-flex aling-items-center justify-content-around flex-wrap">
            <FontAwesomeIcon
              icon={faPhoneAlt}
              style={{ width: 20, height: 18, marginTop: '4px' }}
              className={phoneIsPresent ? 'icon-clickable' : 'icon-disabled'}
              onClick={handleTelButton}
            />
            <FontAwesomeIcon
              icon={faWhatsapp}
              style={{ width: 20, height: 24, marginLeft: '5px' }}
              className={phoneIsPresent ? 'icon-clickable' : 'icon-disabled'}
              onClick={handleWhatsappButton}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <p className="label mb-0">{formatMessage({ id: addressLabel })}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <p className="paragraph1 mb-1">
              {fallbackTextProp(destinationAddress, formatMessage({ id: 'unavailable' }))}
            </p>
          </Col>
          <Col xs={4} className="text-right">
            <FontAwesomeIcon
              icon={faMapMarked}
              className={destinationIsPresent ? 'icon-clickable' : 'icon-disabled'}
              style={{ width: 26, height: 26 }}
              onClick={handleMapButton}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <p className="label mb-0">{formatMessage({ id: notesLabel })}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <p className="paragraph1 mb-1">
              {fallbackTextProp(deliveryOrder.notes, formatMessage({ id: 'unavailable' }))}
            </p>
          </Col>
        </Row>
        <Row className="fix-bottom">
          <Col>{drawPauseButton()}</Col>
          <Col xs={{ span: 6 }} className="text-right">
            {drawActionButton()}
          </Col>
        </Row>
      </div>
    </Col>
  );
};

export default DeliveryRequestListItem;
